import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateTimePicker } from "@/components/DateTimePicker";
import { toast } from "sonner";

const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const blank = () => ({ file_no: "", patient_name: "", admitted_at: nowLocal(), branch_id: "", payer_type: "cash", scheme_id: "", member_no: "",
  customer_id: "", corporate_member_id: "", ward: "", bed: "", doctor: "", notes: "" });

const Pick = ({ label, value, onChange, items, testId, placeholder }) => (
  <div><Label className="text-xs">{label}</Label>
    <Select value={value} onValueChange={onChange}><SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
);

export function AdmitDialog({ open, onOpenChange, onAdmitted }) {
  const { branches } = useBranch();
  const [f, setF] = useState(blank());
  const [schemes, setSchemes] = useState([]);
  const [corps, setCorps] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  useEffect(() => {
    if (!open) return;
    setF({ ...blank(), branch_id: branches.length === 1 ? branches[0].id : "" });
    api.get("/insurance-schemes").then((r) => setSchemes(r.data)).catch(() => {});
    api.get("/customers", { params: { type: "corporate" } }).then((r) => setCorps(r.data)).catch(() => {});
  }, [open, branches]);
  const company = corps.find((c) => c.id === f.customer_id);
  const members = (company?.members || []).filter((m) => m.is_active ?? true);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, admitted_at: f.admitted_at ? new Date(f.admitted_at).toISOString() : null };
      ["scheme_id", "customer_id", "corporate_member_id"].forEach((k) => { if (!body[k]) body[k] = null; });
      const { data } = await api.post("/inpatients", body);
      toast.success(`${data.patient_name} admitted (${data.code})`);
      onOpenChange(false); onAdmitted(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Admit patient</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Hospital file number *</Label><Input value={f.file_no} onChange={set("file_no")} data-testid="ip-file-no" /></div>
          <div><Label className="text-xs">Patient name *</Label><Input value={f.patient_name} onChange={set("patient_name")} data-testid="ip-patient-name" /></div>
          <div><Label className="text-xs">Admitted at *</Label><DateTimePicker value={f.admitted_at} onChange={set("admitted_at")} testId="ip-admitted-at" /></div>
          <Pick label="Dispensing branch *" value={f.branch_id} onChange={set("branch_id")} testId="ip-branch" placeholder="Select branch" items={branches.map((b) => [b.id, b.name])} />
          <Pick label="Payment type *" value={f.payer_type} onChange={(v) => setF((x) => ({ ...x, payer_type: v, scheme_id: "", customer_id: "", corporate_member_id: "" }))} testId="ip-payer"
            items={[["cash", "Cash / self-pay"], ["insurance", "Insurance"], ["corporate", "Corporate"]]} />
          {f.payer_type === "insurance" && <>
            <Pick label="Insurance scheme *" value={f.scheme_id} onChange={set("scheme_id")} testId="ip-scheme" placeholder="Select scheme" items={schemes.map((s) => [s.id, s.name])} />
            <div><Label className="text-xs">Member number</Label><Input value={f.member_no} onChange={set("member_no")} data-testid="ip-member-no" /></div>
          </>}
          {f.payer_type === "corporate" && <>
            <Pick label="Corporate client *" value={f.customer_id} onChange={(v) => setF((x) => ({ ...x, customer_id: v, corporate_member_id: "" }))} testId="ip-company" placeholder="Select company" items={corps.map((c) => [c.id, c.name])} />
            {members.length > 0 && <Pick label="Member *" value={f.corporate_member_id} onChange={set("corporate_member_id")} testId="ip-corp-member" placeholder="Select member"
              items={members.map((m) => [m.id, `${m.name}${m.staff_no ? ` · ${m.staff_no}` : ""}`])} />}
          </>}
          <div><Label className="text-xs">Ward</Label><Input value={f.ward} onChange={set("ward")} data-testid="ip-ward" /></div>
          <div><Label className="text-xs">Bed</Label><Input value={f.bed} onChange={set("bed")} data-testid="ip-bed" /></div>
          <div><Label className="text-xs">Attending doctor</Label><Input value={f.doctor} onChange={set("doctor")} data-testid="ip-doctor" /></div>
          <div><Label className="text-xs">Notes</Label><Input value={f.notes} onChange={set("notes")} data-testid="ip-notes" /></div>
        </div>
        <DialogFooter><Button disabled={busy || !f.file_no.trim() || !f.patient_name.trim() || !f.branch_id} onClick={save} data-testid="ip-admit-save">{busy ? "Saving…" : "Admit patient"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
